import moment from 'moment-timezone'
import { Maybe } from 'types/graphqlTypes'
import { getTimeAgoShort } from 'utils/formatDatetime'
import DATETIME, { FORMATS } from 'constants/datetime'
import { LEAVING_LABELS, KEYED_SHIPMENT_STATUSES } from 'constants/shipments'

const tz = DATETIME.defaultTimeZone

/**
 * Returns className value for status
 * @example appClass('confirmed') // 'text-default'
 */
export const apptClass = (apptStatus: string): string => {
  if (apptStatus === 'confirmed') return 'text-default'
  return 'text-warning'
}

/**
 * @example isToday(moment()) // true
 */
export const isToday = (date: moment.Moment | string): boolean => {
  return moment().isSame(date, 'd')
}

/**
 * Returns format based on whether the stop is today or another date
 * @example stopDate(moment().format(), 'America/New_York') // 'Today'
 */
export const stopDate = (
  startAt: Maybe<string>,
  timezone: string = tz,
  format: string = DATETIME.dayFormat
) => {
  if (!startAt) return null
  return isToday(startAt) ? 'Today' : moment.tz(startAt, timezone).format(format)
}

/**
 * Returns day of week
 * @example stopDayOfWeek(moment().format(), 'America/New_York') // 'Tue'
 */
export const stopDayOfWeek = (startAt: Maybe<string>, timezone: string = tz): string => {
  if (!startAt) {
    return ''
  }
  return moment.tz(startAt, timezone).format(DATETIME.dayOfWeek)
}

/**
 * Returns day of month
 * @example stopDay(moment().format()) // 11
 */
export const stopDay = (startAt: Maybe<string>, timezone: string = tz): string => {
  if (!startAt) {
    return ''
  }
  return moment.tz(startAt, timezone).format(DATETIME.day)
}

/**
 * Returns only timezone abbreviation
 * @example
 * ```
 * stopTimeZone(
 *   '2021-05-12T16:32:26.848Z',
 *   '2021-05-12T16:32:26.848Z',
 *   'America/New_York'
 * ) // EST
 * ```
 */
export const stopTimeZone = (
  startAt: string | undefined,
  endAt: string | undefined,
  timezone: string | undefined
): string => {
  const startOrEndAt = startAt && endAt ? endAt : startAt
  return moment.tz(startOrEndAt, timezone || '').zoneAbbr()
}

/**
 * Returns stop time with optional timezone abbreviation
 * @example
 * ```
 * stopTime(
 *   '2021-05-12T16:32:26.848Z',
 *   '2021-05-12T16:32:26.848Z',
 *   'America/New_York',
 *   true
 * ) // '17:00 EST'
 * ```
 */
export const stopTime = (
  startAt: Maybe<string>,
  endAt: string | undefined,
  timezone: string = tz,
  displayTZ: boolean,
  startTime?: string | null,
  timeFormat?: string | null
): string | null => {
  if (startTime === null) return null
  const format = timeFormat || DATETIME.militaryTimeFormatShort
  const formatWithTz = displayTZ ? `${format} z` : format
  let timeString = ''
  if (startAt) {
    timeString = moment.tz(startAt, timezone).format(formatWithTz)
  }
  if (endAt) {
    const end = moment.tz(endAt, timezone).format(formatWithTz)
    timeString = `${timeString} \u2013 ${end}`
  }
  return timeString
}

export const stopTimeShortNoTimeZone = (
  startAt: Maybe<string>,
  endAt: Maybe<string>,
  timezone: string = tz,
  showTimezone: boolean = false,
  short: boolean = true,
  timeFormat: string = FORMATS.militaryTimeFormatShort12H,
  separator: string = 'to'
): string => {
  const format = `${timeFormat}${showTimezone ? ' z' : ''}`
  const start = startAt ? moment.tz(startAt, timezone) : undefined
  const end = endAt ? moment.tz(endAt, timezone) : undefined
  const timeString = [
    start && start.format(format),
    end && end.format(end.isSame(start, 'd') ? format : `${DATETIME.dayOfWeek} ${format}`),
  ]
    .filter(Boolean)
    .join(` ${separator} `)
    .replace(/am/g, 'a')
    .replace(/pm/g, 'p')
  // 24 hour military times should not be shortened.
  if (short) return timeString.replace(/:00/g, '')
  return timeString
}

/**
 * @example isWithinSetHours('2021-05-12T16:32:26.848Z', 2) // false
 */
export const isWithinSetHours = (now: string, hours: number): boolean => {
  const hoursFromNow = moment().add(hours, 'hours')
  const difference = hoursFromNow.diff(now, 'hours', true)
  return difference > hours
}

export const validateCheckin = (state: {
  nextStatus: string
  currentStop: {
    appointment: {
      timezone: string
    }
    status: {
      arrivedAt: string
    }
  }
  checkinDate: string
  checkinTime: string
}): string => {
  const { currentStop, nextStatus } = state
  let invalidCheckin = ''
  if (!state.checkinDate || !state.checkinTime) return invalidCheckin
  const { timezone } = currentStop.appointment
  const now = moment()
  const nowInStopTimezone = moment.tz(now, timezone)
  const arrivalAtFacility = moment.tz(`${currentStop.status.arrivedAt}`, timezone)
  const actionTime =
    state.checkinDate && state.checkinTime
      ? moment.tz(
          `${state.checkinDate} ${state.checkinTime}`,
          DATETIME.datetimeInputFormat,
          timezone
        )
      : ''
  if (!actionTime) return 'Invalid date and time.'

  // TODO: TO REVIEW
  /* At pick up or delivery change warning note
    Ensure Pick up & Delivery time occur only after facility arrival time */
  const isPast = moment(nowInStopTimezone).isBefore(actionTime)
  if (LEAVING_LABELS.includes(nextStatus)) {
    const actionPreceedsArrival = moment(actionTime).isBefore(arrivalAtFacility)
    if (nextStatus === KEYED_SHIPMENT_STATUSES.in_transit.label) {
      if (isPast) invalidCheckin = 'Picked up time must be in the past.'
      if (actionPreceedsArrival) invalidCheckin = 'Picked up time must be later than arrival time.'
      if (!isPast && !actionPreceedsArrival) invalidCheckin = ''
    } else if (nextStatus === KEYED_SHIPMENT_STATUSES.delivered.label) {
      if (isPast) invalidCheckin = 'Delivered time must be in the past.'
      if (actionPreceedsArrival) invalidCheckin = 'Delivered time must be later than arrival time.'
      if (!isPast && !actionPreceedsArrival) invalidCheckin = ''
    }
  } else if (isPast) invalidCheckin = 'Picked up time must be in the past.'
  return invalidCheckin
}

export const displayAutoApptStatus = (
  status: string,
  autoAppointment: {
    requestedCount: number
    lastRequestedAt: string
  },
  facilityHasContacts: string,
  canAutoEmail: string,
  stopEditedBy: string
): string[] => {
  const notEditedByCX = !stopEditedBy || stopEditedBy === 'Ima Bot'
  if (
    status !== 'confirmed' &&
    autoAppointment &&
    facilityHasContacts &&
    canAutoEmail &&
    notEditedByCX
  ) {
    if (!autoAppointment.requestedCount) return ['unconfirmed', 'Auto-request will be sent soon']
    if (autoAppointment.lastRequestedAt) {
      const lastSent = moment.duration(moment().diff(autoAppointment.lastRequestedAt))
      if (autoAppointment.requestedCount === 3 && lastSent.asMinutes() > 120) {
        return [
          'notResponding',
          `Scheduler not responding · Last auto-request sent ${getTimeAgoShort(
            autoAppointment.lastRequestedAt
          )}`,
        ]
      }
      return ['unconfirmed', `Auto-requested ${getTimeAgoShort(autoAppointment.lastRequestedAt)}`]
    }
  }
  return []
}
